/*
 * WorkAndBuhin.vue の保存・確定ボタン制御を、作業と部品選択状態から判定する処理として切り出した処理。
 */
import { BuhinDetail } from "@/model/Buhin";
import { Work } from "@/model/Work";

export interface WorkBuhinButtonDisabledState {
  disabledBtnConfirm: boolean;
  disabledBtnNoBuhin: boolean;
}

export interface BuhinSelectionConfirmState {
  disabledBtnConfirm: boolean;
}

export interface NoBuhinConfirmButtonState {
  isDisabled: boolean;
}

export interface BuhinConfirmButtonState {
  isDisabled: boolean;
}

/*
 * resolveWorkBuhinButtonDisabledState を WorkAndBuhin.vue から分離し、
 * 確定ボタン活性判定 を親側置換や単体テストで固定するため。
 */
export function resolveWorkBuhinButtonDisabledState(params: {
  work?: Work;
  data: any;
  isManualWork: boolean;
  activeWorkIndex: number;
  listSelectedBuhin: BuhinDetail[];
  listManualBuhin: BuhinDetail[];
}): WorkBuhinButtonDisabledState {
  if (params.work && params.work.buhinList) {
    return resolveWorkButtonDisabledState(
      params.work,
      params.listSelectedBuhin
    );
  }

  if (hasWorkLists(params.data)) {
    if (params.data.seibiList.length == 0 && params.data.manualSeibiList.length == 0) {
      return {
        disabledBtnConfirm: true,
        disabledBtnNoBuhin: true
      };
    }
    const activeWork = params.data.seibiList[params.activeWorkIndex];
    if (
      !params.isManualWork &&
      params.data.seibiList.length > 0 &&
      activeWork &&
      activeWork.disabledSave
    ) {
      return {
        disabledBtnConfirm: true,
        disabledBtnNoBuhin: true
      };
    }
  }

  return {
    disabledBtnConfirm: isBuhinSelectionEmpty(
      params.listSelectedBuhin,
      params.listManualBuhin
    ),
    disabledBtnNoBuhin: false
  };
}

/*
 * resolveWorkButtonDisabledState を WorkAndBuhin.vue から分離し、
 * 確定ボタン活性判定 を親側置換や単体テストで固定するため。
 */
export function resolveWorkButtonDisabledState(
  work: Work,
  listSelectedBuhin: BuhinDetail[]
): WorkBuhinButtonDisabledState {
  const manualBuhinList = Array.isArray(work.manualBuhinList)
    ? work.manualBuhinList
    : [];
  const buhinList = Array.isArray(work.buhinList) ? work.buhinList : [];
  const manualBuhinExist = manualBuhinList.some(
    (buhin: BuhinDetail) => buhin.quantity > 0
  );
  if (work.disabledSave) {
    return {
      disabledBtnConfirm: true,
      disabledBtnNoBuhin: true
    };
  }
  if (buhinList.length > 0 || manualBuhinExist) {
    if (work.isExtreme && work.isExtreme > 0) {
      const selectedBuhinExist = listSelectedBuhin.some(
        (buhin: BuhinDetail) => buhin.quantity > 0
      );
      return {
        disabledBtnConfirm: !selectedBuhinExist && !manualBuhinExist,
        disabledBtnNoBuhin: false
      };
    }
    return {
      disabledBtnConfirm: false,
      disabledBtnNoBuhin: false
    };
  }
  if (work.osusumeBuhinList != null && work.osusumeBuhinList.length > 0) {
    return {
      disabledBtnConfirm: !hasSelectedOsusumeBuhin(work.osusumeBuhinList),
      disabledBtnNoBuhin: false
    };
  }
  return {
    disabledBtnConfirm: true,
    disabledBtnNoBuhin: false
  };
}

/*
 * isBuhinSelectionEmpty を WorkAndBuhin.vue から分離し、
 * 確定ボタン活性判定 を親側置換や単体テストで固定するため。
 */
export function isBuhinSelectionEmpty(
  listSelectedBuhin: BuhinDetail[],
  listManualBuhin: BuhinDetail[]
): boolean {
  if (listSelectedBuhin.length > 0) {
    return listSelectedBuhin.every((buhin: BuhinDetail) => buhin.quantity == 0);
  }
  return listManualBuhin.every((buhin: BuhinDetail) => buhin.quantity == 0);
}

/*
 * buildBuhinSelectionConfirmState を WorkAndBuhin.vue から分離し、
 * 選択済み部品/手入力部品の数量から confirm disabled state を固定するため。
 */
export function buildBuhinSelectionConfirmState(params: {
  listSelectedBuhin: BuhinDetail[];
  listManualBuhin: BuhinDetail[];
}): BuhinSelectionConfirmState {
  return {
    disabledBtnConfirm: isBuhinSelectionEmpty(
      params.listSelectedBuhin,
      params.listManualBuhin
    )
  };
}

/*
 * buildNoBuhinConfirmButtonState を WorkAndBuhin.vue から分離し、
 * 部品なし確定ボタンの部品なし禁止状態と未選択作業の disabled 条件を固定するため。
 */
export function buildNoBuhinConfirmButtonState(params: {
  disabledBtnNoBuhin: boolean;
  activeWorkIndex: number;
}): NoBuhinConfirmButtonState {
  return {
    isDisabled: params.disabledBtnNoBuhin || params.activeWorkIndex == -1
  };
}

/*
 * buildBuhinConfirmButtonState を WorkAndBuhin.vue から分離し、
 * 部品確定ボタンの部品選択不足状態と未選択作業の disabled 条件を固定するため。
 */
export function buildBuhinConfirmButtonState(params: {
  disabledBtnConfirm: boolean;
  activeWorkIndex: number;
}): BuhinConfirmButtonState {
  return {
    isDisabled: params.disabledBtnConfirm || params.activeWorkIndex == -1
  };
}

/*
 * hasSelectedOsusumeBuhin を WorkAndBuhin.vue から分離し、
 * 確定ボタン活性判定 を親側置換や単体テストで固定するため。
 */
export function hasSelectedOsusumeBuhin(osusumeBuhinList: BuhinDetail[]): boolean {
  return osusumeBuhinList.some(
    (buhin: any) => buhin.isDelete != 1 && (buhin.isSelected || buhin.buhinId)
  );
}

function hasWorkLists(data: any): boolean {
  return !!(data && data.seibiList && data.manualSeibiList);
}
